import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, FileSignature } from "lucide-react";
import { get, post, toastError } from "@/lib/api";
import { fmtDate, todayISO, addDaysISO } from "@/lib/format";
import { label } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { PageHeader, Toolbar, FilterToggle, SearchInput } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/States";
import { CustomerSelect, ProjectSelect, EnumSelect } from "@/components/common/Pickers";
import { Money } from "@/components/finance/FinanceBits";
import { LinesEditor, emptyLine } from "@/components/finance/LinesEditor";
import { compactKr } from "@/components/finance/money";

const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "aktiv", label: "Aktive", status: "aktiv" },
  { id: "signert", label: "Signert", status: "signert" },
  { id: "expiring", label: "Utløper ≤ 90 d", expiring: 90 },
  { id: "utkast", label: "Utkast", status: "utkast" },
  { id: "avsluttet", label: "Avsluttet", status: "avsluttet,utlopt,oppsagt" },
];

export default function ContractsPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const filterId = sp.get("expiring") ? "expiring" : FILTERS.find((f) => f.status === sp.get("status"))?.id || "all";
  const filter = FILTERS.find((f) => f.id === filterId) || FILTERS[0];
  const params = { ...(filter.status ? { status: filter.status } : {}), ...(filter.expiring ? { expiring_days: filter.expiring } : {}), ...(sp.get("customer_id") ? { customer_id: sp.get("customer_id") } : {}) };
  const list = useQuery({ queryKey: ["finance", "contracts", params], queryFn: () => get("/finance/contracts", params) });
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list.data || []).filter((c) => !s || [c.contract_number, c.title, c.customer_name, c.project_name, c.po_number].some((x) => (x || "").toLowerCase().includes(s)));
  }, [list.data, q]);
  const kpi = useMemo(() => {
    const a = list.data || [];
    const active = a.filter((c) => c.status === "aktiv");
    return { mrr: active.reduce((s, c) => s + (c.monthly_value || 0), 0), total: a.reduce((s, c) => s + (c.total_value || 0), 0), active: active.length };
  }, [list.data]);
  const setFilter = (f) => {
    const n = new URLSearchParams(sp);
    n.delete("status");
    n.delete("expiring");
    if (f.status) n.set("status", f.status);
    if (f.expiring) n.set("expiring", String(f.expiring));
    setSp(n, { replace: true });
  };
  const newOpen = sp.get("ny") === "1";
  const setNewOpen = (v) => {
    const n = new URLSearchParams(sp);
    v ? n.set("ny", "1") : n.delete("ny");
    setSp(n, { replace: true });
  };
  const today = todayISO();
  const columns = [
    { key: "contract_number", header: "Kontrakt", render: (r) => (
        <div className="min-w-0">
          <div className="font-medium tabular">{r.contract_number}</div>
          <div className="max-w-[320px] truncate text-[12px] text-muted-foreground">{r.title}</div>
        </div>
      ) },
    { key: "customer_name", header: "Kunde", render: (r) => <span className="whitespace-nowrap">{r.customer_name}</span> },
    { key: "type", header: "Type", className: "hidden md:table-cell", headClassName: "hidden md:table-cell", render: (r) => <span className="text-muted-foreground">{label(r.type)}</span> },
    { key: "period", header: "Periode", render: (r) => {
        const days = r.end_date ? Math.round((new Date(r.end_date) - new Date(today)) / 86400000) : null;
        return (
          <span className="whitespace-nowrap tabular">
            {fmtDate(r.start_date, "d. MMM yy")} – {r.end_date ? fmtDate(r.end_date, "d. MMM yy") : "løpende"}
            {r.status === "aktiv" && days != null && days <= 90 && days >= 0 && <span className="ml-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">{days} d</span>}
          </span>
        );
      } },
    { key: "monthly_value", header: "Per måned", align: "right", render: (r) => <Money v={r.monthly_value} muted={!r.monthly_value} /> },
    { key: "total_value", header: "Totalverdi", align: "right", className: "hidden lg:table-cell", headClassName: "hidden lg:table-cell", render: (r) => <Money v={r.total_value} muted={!r.total_value} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} entity="contract" /> },
  ];
  return (
    <div data-testid="contracts-page">
      <PageHeader
        title="Kontrakter"
        count={list.data?.length}
        subtitle={list.data ? <span className="tabular">{kpi.active} aktive · {compactKr(kpi.mrr)} per måned · {compactKr(kpi.total)} samlet kontraktsverdi i utvalget</span> : null}
        actions={can("finance:write") && <Button onClick={() => setNewOpen(true)} data-testid="contracts-new-button"><Plus /> Ny kontrakt</Button>}
        testId="contracts-header"
      />
      <Toolbar className="mt-5" right={<SearchInput value={q} onChange={setQ} placeholder="Søk nummer, tittel, kunde, PO…" testId="contracts-search" />}>
        {FILTERS.map((f) => (
          <FilterToggle key={f.id} pressed={filter.id === f.id} onClick={() => setFilter(f)} testId={`contracts-filter-${f.id}`}>{f.label}</FilterToggle>
        ))}
      </Toolbar>
      <DataTable className="mt-4" columns={columns} rows={rows} isLoading={list.isLoading} error={list.error} onRetry={list.refetch} onRowClick={(r) => navigate(`/okonomi/kontrakter/${r.id}`)} rowTestId={(r) => `contract-row-${r.contract_number}`}
        empty={<EmptyState icon={FileSignature} title="Ingen kontrakter" description="Kontrakter opprettes når et tilbud aksepteres, eller manuelt her." />} testId="contracts-table" />
      <NewContractSheet open={newOpen} onOpenChange={setNewOpen} onCreated={(c) => navigate(`/okonomi/kontrakter/${c.id}`)} />
    </div>
  );
}

export const NewContractSheet = ({ open, onOpenChange, onCreated, defaults = {} }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ customer_id: defaults.customer_id || null, project_id: defaults.project_id || null, title: "", type: "leie", start_date: todayISO(), end_date: addDaysISO(todayISO(), 180), notice_period_days: 90, billing_cadence: "manedlig", payment_terms_days: "", indexation_type: "kpi", guarantee_type: "ingen", po_number: "", inclusions: "", exclusions: "", lines: [emptyLine(true)] });
  const create = useMutation({
    mutationFn: () => post("/finance/contracts", { ...form, payment_terms_days: form.payment_terms_days ? Number(form.payment_terms_days) : null, notice_period_days: Number(form.notice_period_days) || 0, lines: form.lines.filter((l) => l.description.trim()).map((l) => ({ kind: l.kind, description: l.description, quantity: Number(l.quantity) || 0, unit: l.unit, unit_price: Number(l.unit_price) || 0, recurring: !!l.recurring })) }),
    onSuccess: (c) => {
      toast.success(`Kontrakt ${c.contract_number} opprettet`, { description: `${compactKr(c.monthly_value)}/mnd · utkast` });
      qc.invalidateQueries({ queryKey: ["finance"] });
      onOpenChange(false);
      onCreated?.(c);
    },
    onError: (e) => toastError(e, "Kunne ikke opprette kontrakt"),
  });
  const valid = form.customer_id && form.title.trim() && form.start_date && form.lines.some((l) => l.description.trim());
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-[760px] max-w-[96vw] flex-col gap-0 p-0" data-testid="new-contract-sheet">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>Ny kontrakt</SheetTitle>
          <SheetDescription>Opprettes som utkast. Løpende linjer faktureres per periode; engangslinjer ved oppstart/avslutning.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5 scrollbar-thin">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Kunde</Label><CustomerSelect value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v })} testId="new-contract-customer" /></div>
            <div className="space-y-1.5"><Label>Prosjekt</Label><ProjectSelect value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testId="new-contract-project" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="c-title">Tittel</Label><Input id="c-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Leieavtale …" data-testid="new-contract-title" /></div>
            <div className="space-y-1.5"><Label>Type</Label><EnumSelect value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={["leie", "rammeavtale", "service", "salg"]} labelFn={label} testId="new-contract-type" /></div>
            <div className="space-y-1.5"><Label>Fakturering</Label><EnumSelect value={form.billing_cadence} onChange={(v) => setForm({ ...form, billing_cadence: v })} options={["manedlig", "kvartalsvis", "engang"]} labelFn={label} testId="new-contract-cadence" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-start">Start</Label><Input id="c-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="new-contract-start" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-end">Slutt</Label><Input id="c-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="new-contract-end" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-notice">Oppsigelsestid (dager)</Label><Input id="c-notice" type="number" value={form.notice_period_days} onChange={(e) => setForm({ ...form, notice_period_days: e.target.value })} data-testid="new-contract-notice" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-terms">Betalingsfrist (dager)</Label><Input id="c-terms" type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} placeholder="Kundens standard" data-testid="new-contract-terms" /></div>
            <div className="space-y-1.5"><Label>Indeksregulering</Label><EnumSelect value={form.indexation_type} onChange={(v) => setForm({ ...form, indexation_type: v })} options={["kpi", "fast", "ingen"]} labelFn={label} testId="new-contract-index" /></div>
            <div className="space-y-1.5"><Label>Garanti</Label><EnumSelect value={form.guarantee_type} onChange={(v) => setForm({ ...form, guarantee_type: v })} options={["ingen", "depositum", "bankgaranti", "morselskapsgaranti"]} labelFn={label} testId="new-contract-guarantee" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="c-po">Bestillingsnummer (PO)</Label><Input id="c-po" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="new-contract-po" /></div>
          </div>
          <div><Label className="mb-2 block">Linjer</Label><LinesEditor lines={form.lines} onChange={(lines) => setForm({ ...form, lines })} withRecurring /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="c-inc">Inkludert</Label><Textarea id="c-inc" rows={2} value={form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} data-testid="new-contract-inclusions" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-exc">Ikke inkludert</Label><Textarea id="c-exc" rows={2} value={form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} data-testid="new-contract-exclusions" /></div>
          </div>
        </div>
        <SheetFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={() => create.mutate()} disabled={!valid || create.isPending} data-testid="new-contract-submit">Opprett utkast</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
